(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-product-image-bra]';
  const SLIDER_SELECTOR = '.slider-wrapper';
  const TRACK_SELECTOR = '.slider-track';
  const ITEM_SELECTOR = '.feature-item';
  const PREVIOUS_SELECTOR = '.prev-btn';
  const NEXT_SELECTOR = '.next-btn';

  const MOBILE_MEDIA_QUERY = window.matchMedia(
    '(max-width: 767px)'
  );

  const SWIPE_THRESHOLD = 50;

  function initSlider(wrapper) {
    if (!wrapper) return;

    if (wrapper.dataset.sliderInitialized === 'true') {
      return;
    }

    const track = wrapper.querySelector(TRACK_SELECTOR);
    const items = Array.from(
      wrapper.querySelectorAll(ITEM_SELECTOR)
    );
    const previousButton = wrapper.querySelector(
      PREVIOUS_SELECTOR
    );
    const nextButton = wrapper.querySelector(
      NEXT_SELECTOR
    );

    if (!track || items.length === 0) {
      wrapper.classList.add('slider-wrapper--empty');

      if (previousButton) {
        previousButton.hidden = true;
      }

      if (nextButton) {
        nextButton.hidden = true;
      }

      return;
    }

    wrapper.dataset.sliderInitialized = 'true';

    const eventController = new AbortController();
    const eventSignal = eventController.signal;

    let currentIndex = 0;
    let currentTranslate = 0;
    let dragOffset = 0;
    let dragStartX = 0;
    let activePointerId = null;
    let isDragging = false;
    let resizeTimer = null;
    let resizeObserver = null;

    function isMobile() {
      return MOBILE_MEDIA_QUERY.matches;
    }

    function getItemWidth() {
      const firstItem = items[0];

      if (!firstItem) return 0;

      return firstItem.getBoundingClientRect().width;
    }

    function getMaximumIndex() {
      return Math.max(0, items.length - 1);
    }

    function clampIndex(index) {
      return Math.max(
        0,
        Math.min(index, getMaximumIndex())
      );
    }

    function updateButtons() {
      if (previousButton) {
        previousButton.disabled =
          !isMobile() || currentIndex <= 0;

        previousButton.setAttribute(
          'aria-disabled',
          String(previousButton.disabled)
        );
      }

      if (nextButton) {
        nextButton.disabled =
          !isMobile() ||
          currentIndex >= getMaximumIndex();

        nextButton.setAttribute(
          'aria-disabled',
          String(nextButton.disabled)
        );
      }
    }

    function renderSlider(animate = true) {
      if (!isMobile()) {
        currentIndex = 0;
        currentTranslate = 0;

        track.style.transition = '';
        track.style.transform = '';

        updateButtons();
        return;
      }

      currentIndex = clampIndex(currentIndex);

      const itemWidth = getItemWidth();

      if (!itemWidth) return;

      currentTranslate = -(currentIndex * itemWidth);

      track.style.transition = animate
        ? 'transform 0.3s ease'
        : 'none';

      track.style.transform =
        `translate3d(${currentTranslate}px, 0, 0)`;

      updateButtons();
    }

    function goToSlide(index) {
      currentIndex = clampIndex(index);
      renderSlider(true);
    }

    function handlePreviousClick() {
      goToSlide(currentIndex - 1);
    }

    function handleNextClick() {
      goToSlide(currentIndex + 1);
    }

    function handlePointerDown(event) {
      if (!isMobile()) return;

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      isDragging = true;
      dragOffset = 0;
      dragStartX = event.clientX;
      activePointerId = event.pointerId;

      track.style.transition = 'none';
      wrapper.classList.add('is-dragging');

      if (
        typeof wrapper.setPointerCapture === 'function'
      ) {
        wrapper.setPointerCapture(event.pointerId);
      }
    }

    function handlePointerMove(event) {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      dragOffset = event.clientX - dragStartX;

      if (Math.abs(dragOffset) > 5) {
        event.preventDefault();
      }

      track.style.transform =
        `translate3d(${currentTranslate + dragOffset}px, 0, 0)`;
    }

    function handlePointerEnd(event) {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      if (
        typeof wrapper.hasPointerCapture === 'function' &&
        wrapper.hasPointerCapture(event.pointerId)
      ) {
        wrapper.releasePointerCapture(event.pointerId);
      }

      isDragging = false;
      activePointerId = null;

      wrapper.classList.remove('is-dragging');

      if (dragOffset <= -SWIPE_THRESHOLD) {
        currentIndex += 1;
      } else if (dragOffset >= SWIPE_THRESHOLD) {
        currentIndex -= 1;
      }

      dragOffset = 0;

      renderSlider(true);
    }

    function handleResize() {
      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(() => {
        renderSlider(false);
      }, 100);
    }

    function preventNativeDrag(event) {
      event.preventDefault();
    }

    function destroySlider() {
      window.clearTimeout(resizeTimer);

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      eventController.abort();

      track.style.transition = '';
      track.style.transform = '';

      wrapper.classList.remove('is-dragging');
      wrapper.removeAttribute('data-slider-initialized');

      delete wrapper.productImageBraDestroy;
    }

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        handlePreviousClick,
        { signal: eventSignal }
      );
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        handleNextClick,
        { signal: eventSignal }
      );
    }

    wrapper.addEventListener(
      'pointerdown',
      handlePointerDown,
      { signal: eventSignal }
    );

    wrapper.addEventListener(
      'pointermove',
      handlePointerMove,
      { signal: eventSignal }
    );

    wrapper.addEventListener(
      'pointerup',
      handlePointerEnd,
      { signal: eventSignal }
    );

    wrapper.addEventListener(
      'pointercancel',
      handlePointerEnd,
      { signal: eventSignal }
    );

    wrapper.addEventListener(
      'lostpointercapture',
      () => {
        if (!isDragging) return;

        isDragging = false;
        activePointerId = null;
        dragOffset = 0;

        wrapper.classList.remove('is-dragging');
        renderSlider(true);
      },
      { signal: eventSignal }
    );

    wrapper.addEventListener(
      'dragstart',
      preventNativeDrag,
      { signal: eventSignal }
    );

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
        signal: eventSignal
      }
    );

    MOBILE_MEDIA_QUERY.addEventListener(
      'change',
      handleResize,
      { signal: eventSignal }
    );

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });

      resizeObserver.observe(wrapper);
    }

    wrapper
      .querySelectorAll('img')
      .forEach((image) => {
        if (image.complete) return;

        image.addEventListener(
          'load',
          handleResize,
          {
            once: true,
            signal: eventSignal
          }
        );
      });

    wrapper.productImageBraDestroy = destroySlider;

    renderSlider(false);
  }

  function initProductImageBra(root = document) {
    if (root.matches?.(SLIDER_SELECTOR)) {
      initSlider(root);
    }

    root
      .querySelectorAll?.(SLIDER_SELECTOR)
      .forEach(initSlider);
  }

  function destroyProductImageBra(root) {
    const wrappers = [];

    if (root.matches?.(SLIDER_SELECTOR)) {
      wrappers.push(root);
    }

    root
      .querySelectorAll?.(SLIDER_SELECTOR)
      .forEach((wrapper) => {
        wrappers.push(wrapper);
      });

    wrappers.forEach((wrapper) => {
      if (
        typeof wrapper.productImageBraDestroy ===
        'function'
      ) {
        wrapper.productImageBraDestroy();
      }
    });
  }

  function handleDocumentReady() {
    initProductImageBra(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      handleDocumentReady,
      { once: true }
    );
  } else {
    handleDocumentReady();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initProductImageBra(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyProductImageBra(event.target);
    }
  );
})();